// src/pages/SowTemplatePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { sowTemplateApi as st, getApiError } from "../api/client";
import "../styles/sowTemplate.css";

function Modal({ open, title, onClose, children, width = 900 }) {
  if (!open) return null;
  return (
    <div className="st-modal" onMouseDown={onClose}>
      <div
        className="st-modal-inner"
        style={{ maxWidth: width }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="st-row st-between">
          <div className="st-h3">{title}</div>
          <button className="st-btn ghost" onClick={onClose}>
            Close
          </button>
        </div>
        <div style={{ marginTop: 12 }}>{children}</div>
      </div>
    </div>
  );
}

// Support both {{placeholder}} and <Placeholder> formats
const PLACEHOLDER_REGEX = /(?:\{\{\s*([a-zA-Z0-9_]+)\s*\}\})|(?:<([A-Za-z][A-Za-z0-9_ ]*[A-Za-z0-9])>)/g;

function extractPlaceholdersFromText(txt) {
  const out = new Set();
  const s = String(txt || "");
  let m;
  const regex = new RegExp(PLACEHOLDER_REGEX.source, 'g');
  while ((m = regex.exec(s)) !== null) {
    const ph = m[1] || m[2];
    if (ph) {
      const normalized = ph.replace(/\s+/g, '_').toLowerCase();
      out.add(normalized);
    }
  }
  return Array.from(out);
}

function safeKeyFromTitle(title) {
  return (
    String(title || "")
      .trim()
      .toLowerCase()
      .replace(/^[\d.]+\s*/, "")
      .replace(/[^a-z0-9_ ]/g, "")
      .replace(/\s+/g, "_")
      .slice(0, 50) || "section"
  );
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function extractSectionNumber(title) {
  const match = String(title || "").match(/^(\d+)\.?\s*/);
  return match ? match[1] : null;
}

export default function SowTemplatePage() {
  const [q, setQ] = useState("");
  const [contractType, setContractType] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [tpl, setTpl] = useState(null);
  const [selectedSectionKey, setSelectedSectionKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const [docxFile, setDocxFile] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadVersion, setUploadVersion] = useState("1.0");
  const [uploadContractType, setUploadContractType] = useState("");

  const [phOpen, setPhOpen] = useState(false);
  const [phEditKey, setPhEditKey] = useState("");
  const [phKey, setPhKey] = useState("");
  const [phLabel, setPhLabel] = useState("");
  const [phType, setPhType] = useState("string");
  const [phRequired, setPhRequired] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);

  const editorRef = useRef(null);

  async function refreshList() {
    setBusy(true);
    setMsg("");
    try {
      const res = await st.list(q, contractType);
      setRows(res || []);
    } catch (e) {
      setMsg(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    refreshList();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => refreshList(), 300);
    return () => clearTimeout(t);
  }, [q, contractType]);

  async function loadTemplate(id) {
    setBusy(true);
    setMsg("");
    try {
      const full = await st.get(id);

      const tags =
        full.tags_schema_json && typeof full.tags_schema_json === "object"
          ? full.tags_schema_json
          : {};

      const sections = Array.isArray(full.sections_json) ? full.sections_json : [];

      const normalizedSections = sections
        .map((s, i) => ({
          key: (s.key || `section_${i + 1}`).trim(),
          title: (s.title || `Section ${i + 1}`).trim(),
          content_template: s.content_template || "",
          prompt_hint: s.prompt_hint || "",
          order: Number.isFinite(Number(s.order)) ? Number(s.order) : i,
        }))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((s, idx) => ({ ...s, order: idx }));

      const firstKey = normalizedSections[0]?.key || "";
      setTpl({
        id: full.id,
        name: full.name || "",
        version: full.version || "1.0",
        contract_type: full.contract_type || "",
        active: full.active !== false,
        tags_schema_json: tags,
        sections_json: normalizedSections,
        has_docx: !!full.has_docx,
      });
      setSelectedSectionKey(firstKey);
      setSelectedId(id);
    } catch (e) {
      setMsg(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  const selectedSection = useMemo(() => {
    if (!tpl?.sections_json?.length) return null;
    return (
      tpl.sections_json.find((s) => s.key === selectedSectionKey) ||
      tpl.sections_json[0]
    );
  }, [tpl, selectedSectionKey]);

  const allPlaceholders = useMemo(() => {
    const schemaKeys = Object.keys(tpl?.tags_schema_json || {});
    const used = new Set();
    for (const s of tpl?.sections_json || []) {
      extractPlaceholdersFromText(s.content_template).forEach((k) => used.add(k));
      extractPlaceholdersFromText(s.title).forEach((k) => used.add(k));
    }
    const merged = new Set([...schemaKeys, ...Array.from(used)]);
    return Array.from(merged).sort();
  }, [tpl]);

  const unregisteredUsedPlaceholders = useMemo(() => {
    const schema = tpl?.tags_schema_json || {};
    const missing = [];
    const used = new Set();
    for (const s of tpl?.sections_json || []) {
      extractPlaceholdersFromText(s.content_template).forEach((k) => used.add(k));
      extractPlaceholdersFromText(s.title).forEach((k) => used.add(k));
    }
    for (const k of used) {
      if (!schema[k]) missing.push(k);
    }
    return missing.sort();
  }, [tpl]);

  function setSectionField(sectionKey, patch) {
    if (!tpl) return;
    const next = (tpl.sections_json || []).map((s) =>
      s.key === sectionKey ? { ...s, ...patch } : s
    );
    setTpl({ ...tpl, sections_json: next });
  }

  function addSection() {
    if (!tpl) {
      setTpl({
        id: null,
        name: "New Template",
        version: "1.0",
        contract_type: "",
        active: true,
        tags_schema_json: {},
        sections_json: [],
        has_docx: false,
      });
      setSelectedId(null);
      setSelectedSectionKey("");
    }

    const title = prompt("Section title (e.g., '4. Scope Exclusions')?");
    if (!title) return;

    const key0 = safeKeyFromTitle(title);
    const exists = new Set((tpl?.sections_json || []).map((s) => s.key));
    let key = key0;
    let n = 2;
    while (exists.has(key)) key = `${key0}_${n++}`;

    const next = [...(tpl?.sections_json || [])];
    next.push({
      key,
      title,
      content_template: "",
      prompt_hint: "",
      order: next.length,
    });

    setTpl({ ...(tpl || {}), sections_json: next });
    setSelectedSectionKey(key);
  }

  function deleteSection(sectionKey) {
    if (!tpl) return;
    if (!window.confirm("Delete this section?")) return;
    const next = (tpl.sections_json || [])
      .filter((s) => s.key !== sectionKey)
      .map((s, i) => ({ ...s, order: i }));
    setTpl({ ...tpl, sections_json: next });
    setSelectedSectionKey(next[0]?.key || "");
  }

  function moveSection(sectionKey, dir) {
    if (!tpl) return;
    const arr = [...(tpl.sections_json || [])].sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );
    const i = arr.findIndex((s) => s.key === sectionKey);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
    const re = arr.map((s, idx) => ({ ...s, order: idx }));
    setTpl({ ...tpl, sections_json: re });
  }

  function openAddPlaceholder(prefillKey = "") {
    setPhEditKey("");
    setPhKey(prefillKey || "");
    setPhLabel(
      prefillKey
        ? prefillKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : ""
    );
    setPhType("string");
    setPhRequired(false);
    setPhOpen(true);
  }

  function openEditPlaceholder(phK) {
    const schema = tpl?.tags_schema_json || {};
    const meta = schema[phK] || {};
    setPhEditKey(phK);
    setPhKey(phK);
    setPhLabel(meta.label || phK);
    setPhType(meta.type || "string");
    setPhRequired(!!meta.required);
    setPhOpen(true);
  }

  function savePlaceholder() {
    if (!tpl) return;

    const k = String(phKey || "").trim().toLowerCase().replace(/\s+/g, '_');
    if (!k) return setMsg("Placeholder key is required.");
    if (!/^[a-zA-Z0-9_]+$/.test(k)) {
      return setMsg("Placeholder key must be letters/numbers/underscore only.");
    }

    const nextSchema = { ...(tpl.tags_schema_json || {}) };
    nextSchema[k] = {
      type: phType || "string",
      label: phLabel || k,
      required: !!phRequired,
    };

    if (phEditKey && phEditKey !== k) {
      delete nextSchema[phEditKey];
      const nextSections = (tpl.sections_json || []).map((s) => ({
        ...s,
        content_template: String(s.content_template || "")
          .replaceAll(`{{${phEditKey}}}`, `<${phLabel || k}>`)
          .replaceAll(`<${phEditKey}>`, `<${phLabel || k}>`),
      }));
      setTpl({ ...tpl, tags_schema_json: nextSchema, sections_json: nextSections });
    } else {
      setTpl({ ...tpl, tags_schema_json: nextSchema });
    }

    setPhOpen(false);
  }

  function deletePlaceholder(phK) {
    if (!tpl) return;
    if (!window.confirm(`Delete placeholder "${phK}" from schema?`)) return;
    const nextSchema = { ...(tpl.tags_schema_json || {}) };
    delete nextSchema[phK];
    setTpl({ ...tpl, tags_schema_json: nextSchema });
  }

  function insertPh(phK) {
    if (!tpl) return;

    const schema = tpl?.tags_schema_json || {};
    const meta = schema[phK] || {};
    const label = meta.label || phK.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const token = `<${label}>`;
    
    const currentKey = selectedSection?.key || tpl.sections_json?.[0]?.key;
    if (!currentKey) return setMsg("No section exists. Please add a section first.");

    const section = (tpl.sections_json || []).find((s) => s.key === currentKey);
    const cur = section?.content_template || "";

    const el = editorRef.current;
    if (!el) {
      setSectionField(currentKey, { content_template: cur + (cur ? "\n" : "") + token });
      return;
    }

    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? start;
    const next = cur.slice(0, start) + token + cur.slice(end);

    setSectionField(currentKey, { content_template: next });

    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  async function saveTemplate() {
    if (!tpl) return;
    setBusy(true);
    setMsg("");
    try {
      const body = {
        name: tpl.name,
        contract_type: tpl.contract_type || null,
        version: tpl.version || "1.0",
        active: tpl.active !== false,
        tags_schema_json: tpl.tags_schema_json || {},
        sections_json: (tpl.sections_json || []).map((s) => ({
          key: s.key,
          title: s.title,
          content_template: s.content_template || "",
          prompt_hint: s.prompt_hint || "",
          order: s.order ?? 0,
        })),
      };

      const saved = tpl.id
        ? await st.update(tpl.id, body)
        : await st.create(body);

      setMsg("Template saved.");
      await refreshList();
      await loadTemplate(saved.id);
    } catch (e) {
      setMsg(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function deleteTemplate() {
    if (!tpl?.id) return;
    if (!window.confirm("Delete this template?")) return;
    setBusy(true);
    setMsg("");
    try {
      await st.delete(tpl.id);
      setTpl(null);
      setSelectedId(null);
      setSelectedSectionKey("");
      await refreshList();
      setMsg("Template deleted.");
    } catch (e) {
      setMsg(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function uploadDocx() {
    if (!docxFile) return;
    const name = (uploadName || "").trim();
    if (!name) return setMsg("Please enter Template Name for upload.");

    setBusy(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("version", uploadVersion || "1.0");
      if ((uploadContractType || "").trim())
        fd.append("contract_type", uploadContractType.trim());
      fd.append("active", "true");
      fd.append("file", docxFile);

      const saved = await st.uploadDocx(fd);
      setMsg("Uploaded & parsed DOCX.");
      setDocxFile(null);
      setUploadName("");
      await refreshList();
      await loadTemplate(saved.id);
    } catch (e) {
      setMsg(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function downloadTemplateDocx() {
    if (!tpl?.id) return;
    if (!tpl?.has_docx) return setMsg("No DOCX available for this template.");

    setBusy(true);
    setMsg("");
    try {
      const blob = await st.downloadDocx(tpl.id);
      const safeName = String(tpl.name || "template").replace(/[\/\\:*?"<>|]/g, "_");
      downloadBlob(blob, `${safeName}_v${tpl.version || "1.0"}.docx`);
    } catch (e) {
      setMsg(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  function renderPreviewHTML() {
    const t = tpl;
    if (!t) return "<div class='st-muted'>No template selected.</div>";

    const esc = (s) =>
      String(s || "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;");

    const renderText = (txt) => {
      let html = esc(txt);
      html = html.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, k) => {
        return `<span class="st-ph" title="Placeholder: ${esc(k)}">{{${esc(k)}}}</span>`;
      });
      html = html.replace(/&lt;([A-Za-z][A-Za-z0-9_ ]*[A-Za-z0-9])&gt;/g, (_m, k) => {
        return `<span class="st-ph" title="Placeholder: ${k}">&lt;${k}&gt;</span>`;
      });
      return html.replaceAll("\n", "<br/>");
    };

    const parts = [];
    
    parts.push(`
      <div class="sow-doc">
        <div class="sow-header">
          <div class="sow-logo">
            <img src="/logo.png" alt="Logo" class="sow-logo-img" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
            <div class="sow-logo-placeholder" style="display:none;">LOGO</div>
          </div>
          <div class="sow-title-block">
            <div class="sow-project-name">${renderText('<Project Name>')}</div>
            <div class="sow-for">for ${renderText('<Client Name>')}</div>
            <div class="sow-subtitle">Statement of Work for Professional Services</div>
          </div>
        </div>
    `);
    
    parts.push(`
      <table class="sow-exec-table">
        <tr><td colspan="4" class="sow-exec-header">Executive Summary</td></tr>
        <tr><td class="sow-label">Customer</td><td colspan="3">${renderText('<Client Name>')}</td></tr>
        <tr>
          <td class="sow-label">Scope of Services</td>
          <td colspan="3">This Statement of Work ("SOW") outlines the professional services to be performed by Jade Global for ${renderText('<Client Name>')}.</td>
        </tr>
        <tr>
          <td class="sow-label">Schedule</td>
          <td><strong>Start:</strong> ${renderText('<Start Date>')}</td>
          <td><strong>End:</strong> ${renderText('<End Date>')}</td>
          <td></td>
        </tr>
        <tr>
          <td class="sow-label">Customer Contact</td>
          <td>${renderText('<Client Contact Name>')}<br/>${renderText('<Client Contact Email>')}</td>
          <td class="sow-label">Jade Contact</td>
          <td>${renderText('<Jade Contact Name>')}<br/>${renderText('<Jade Contact Email>')}</td>
        </tr>
      </table>
    `);
    
    const sections = (t.sections_json || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    
    parts.push(`<div class="sow-toc"><div class="sow-toc-title">TABLE OF CONTENTS</div>`);
    sections.forEach((s, idx) => {
      const num = extractSectionNumber(s.title) || (idx + 1);
      const title = s.title.replace(/^\d+\.?\s*/, '');
      parts.push(`<div class="sow-toc-item"><span class="sow-toc-num">${num}.</span> ${esc(title)}</div>`);
    });
    parts.push(`</div>`);
    
    for (const s of sections) {
      const num = extractSectionNumber(s.title);
      const titleText = s.title.replace(/^\d+\.?\s*/, '');
      parts.push(`
        <div class="sow-section">
          <div class="sow-section-title">${num ? `<span class="sow-section-num">${num}.</span>` : ''} ${esc(titleText)}</div>
          <div class="sow-section-content">${renderText(s.content_template || '') || "<span class='st-muted'>(No content)</span>"}</div>
        </div>
      `);
    }
    
    parts.push(`</div>`);
    return parts.join("");
  }

  return (
    <div className="st-page">
      {/* Header */}
      <div className="st-top">
        <div>
          <div className="st-title">SOW Template Builder</div>
          <div className="st-sub">Upload DOCX or build sections → manage placeholders → Save</div>
        </div>
        <div className="st-actions">
          <button className="st-btn ghost" onClick={refreshList} disabled={busy}>Refresh</button>
          <button className="st-btn ghost" onClick={() => setPreviewOpen(true)} disabled={!tpl}>View Template</button>
          <button className="st-btn ghost" onClick={downloadTemplateDocx} disabled={!tpl?.id || !tpl?.has_docx || busy}>Download DOCX</button>
          <button className="st-btn" onClick={saveTemplate} disabled={!tpl || busy}>Save Template</button>
          <button className="st-btn danger" onClick={deleteTemplate} disabled={!tpl?.id || busy}>Delete</button>
        </div>
      </div>

      {msg && <div className="st-alert">{msg}</div>}

      {/* ROW 1: Templates */}
      <div className="st-card st-row-full">
        <div className="st-row st-between" style={{ marginBottom: 12 }}>
          <div className="st-h3">Templates</div>
          <button className="st-btn ghost" onClick={() => {
            setTpl({ id: null, name: "New Template", version: "1.0", contract_type: "", active: true, tags_schema_json: {}, sections_json: [], has_docx: false });
            setSelectedId(null);
            setSelectedSectionKey("");
          }}>+ New Template</button>
        </div>
        
        <div className="st-row" style={{ gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <input className="st-input" placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 200 }} />
          <input className="st-input" placeholder="Contract Type" value={contractType} onChange={(e) => setContractType(e.target.value)} style={{ maxWidth: 180 }} />
          <div className="st-divider-v" />
          <input className="st-input" placeholder="Upload name" value={uploadName} onChange={(e) => setUploadName(e.target.value)} style={{ maxWidth: 180 }} />
          <input className="st-input" placeholder="Version" value={uploadVersion} onChange={(e) => setUploadVersion(e.target.value)} style={{ maxWidth: 80 }} />
          <input type="file" accept=".docx" onChange={(e) => setDocxFile(e.target.files?.[0] || null)} style={{ maxWidth: 200 }} />
          <button className="st-btn" onClick={uploadDocx} disabled={busy || !docxFile}>Upload DOCX</button>
        </div>

        <div className="st-template-list">
          {rows.map((r) => (
            <div key={r.id} className={"st-template-item " + (selectedId === r.id ? "active" : "")} onClick={() => loadTemplate(r.id)}>
              <div className="st-template-name">{r.name}</div>
              <div className="st-template-meta">{r.contract_type || "GLOBAL"} • v{r.version || "1.0"}</div>
            </div>
          ))}
          {rows.length === 0 && <div className="st-muted">No templates found</div>}
        </div>
      </div>

      {/* ROW 2: Sections */}
      <div className="st-card st-row-full">
        <div className="st-row st-between" style={{ marginBottom: 12 }}>
          <div className="st-h3">Sections {tpl ? `- ${tpl.name}` : ''}</div>
          <div className="st-row" style={{ gap: 8 }}>
            <button className="st-btn ghost" onClick={() => openAddPlaceholder()} disabled={!tpl}>+ Placeholder</button>
            <button className="st-btn ghost" onClick={addSection} disabled={busy}>+ Add Section</button>
          </div>
        </div>

        {!tpl ? (
          <div className="st-muted">Select or create a template above</div>
        ) : (
          <>
            <div className="st-row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <input className="st-input" placeholder="Template name" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} style={{ maxWidth: 250 }} />
              <input className="st-input" placeholder="Version" value={tpl.version} onChange={(e) => setTpl({ ...tpl, version: e.target.value })} style={{ maxWidth: 100 }} />
              <input className="st-input" placeholder="Contract Type" value={tpl.contract_type} onChange={(e) => setTpl({ ...tpl, contract_type: e.target.value })} style={{ maxWidth: 180 }} />
            </div>

            {unregisteredUsedPlaceholders.length > 0 && (
              <div className="st-warn" style={{ marginBottom: 12 }}>
                <b>Unregistered placeholders:</b> {unregisteredUsedPlaceholders.map((k) => (
                  <span key={k} className="st-chip" onClick={() => openAddPlaceholder(k)}>{k}</span>
                ))}
              </div>
            )}

            <div className="st-sections-row">
              {(tpl.sections_json || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((s) => (
                <div key={s.key} className={"st-section-card " + (selectedSectionKey === s.key ? "active" : "")} onClick={() => setSelectedSectionKey(s.key)}>
                  <div className="st-section-title">{s.title}</div>
                  <div className="st-section-meta">Order: {s.order}</div>
                  <div className="st-section-actions">
                    <button className="st-btn mini" onClick={(e) => { e.stopPropagation(); moveSection(s.key, -1); }}>↑</button>
                    <button className="st-btn mini" onClick={(e) => { e.stopPropagation(); moveSection(s.key, +1); }}>↓</button>
                    <button className="st-btn mini danger" onClick={(e) => { e.stopPropagation(); deleteSection(s.key); }}>×</button>
                  </div>
                </div>
              ))}
            </div>

            {allPlaceholders.length > 0 && (
              <div className="st-placeholders-row">
                <span className="st-muted">Placeholders: </span>
                {allPlaceholders.map((k) => (
                  <span key={k} className="st-ph-chip" onClick={() => insertPh(k)} title="Click to insert">{k}</span>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ROW 3: Editor */}
      <div className="st-card st-row-full">
        <div className="st-row st-between" style={{ marginBottom: 12 }}>
          <div className="st-h3">Editor {selectedSection ? `- ${selectedSection.title}` : ''}</div>
          {tpl?.id && <div className="st-muted">Template #{tpl.id}</div>}
        </div>

        {!tpl || !selectedSection ? (
          <div className="st-muted">Select a section above to edit</div>
        ) : (
          <>
            <div className="st-row" style={{ gap: 8, marginBottom: 12 }}>
              <input
                className="st-input"
                value={selectedSection.title}
                onChange={(e) => setSectionField(selectedSection.key, { title: e.target.value })}
                placeholder="Section title (e.g., '3. Scope of Services')"
                style={{ flex: 1 }}
              />
              <input className="st-input" value={selectedSection.key} disabled style={{ maxWidth: 200, opacity: 0.6 }} />
            </div>

            <textarea
              ref={editorRef}
              className="st-textarea-full"
              value={selectedSection.content_template || ""}
              onChange={(e) => setSectionField(selectedSection.key, { content_template: e.target.value })}
              placeholder="Write template text here. Use placeholders like <Client Name> or {{project_name}}"
            />

            <input
              className="st-input"
              value={selectedSection.prompt_hint || ""}
              onChange={(e) => setSectionField(selectedSection.key, { prompt_hint: e.target.value })}
              placeholder="Optional prompt hint for AI drafting"
              style={{ marginTop: 12 }}
            />
          </>
        )}
      </div>

      {/* Preview Modal */}
      <Modal open={previewOpen} title={tpl ? `Template Preview: ${tpl.name}` : "Preview"} onClose={() => setPreviewOpen(false)} width={1000}>
        <div className="st-preview sow-preview" style={{ maxHeight: '75vh', overflowY: 'auto' }} dangerouslySetInnerHTML={{ __html: renderPreviewHTML() }} />
      </Modal>

      {/* Placeholder Modal */}
      <Modal open={phOpen} title={phEditKey ? `Edit Placeholder: ${phEditKey}` : "Add Placeholder"} onClose={() => setPhOpen(false)} width={500}>
        <div className="st-form">
          <label>Key</label>
          <input className="st-input" value={phKey} onChange={(e) => setPhKey(e.target.value)} placeholder="client_name" />
          <label>Label</label>
          <input className="st-input" value={phLabel} onChange={(e) => setPhLabel(e.target.value)} placeholder="Client Name" />
          <div className="st-row" style={{ gap: 12, marginTop: 12 }}>
            <select className="st-input" value={phType} onChange={(e) => setPhType(e.target.value)} style={{ flex: 1 }}>
              <option value="string">String</option>
              <option value="number">Number</option>
              <option value="date">Date</option>
              <option value="email">Email</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={phRequired} onChange={(e) => setPhRequired(e.target.checked)} /> Required
            </label>
          </div>
          <button className="st-btn" onClick={savePlaceholder} style={{ marginTop: 16, width: '100%' }}>Save Placeholder</button>
        </div>
      </Modal>
    </div>
  );
}